'use client';

/**
 * McpClaudeHero — V3 hero for /mcp-claude.
 *
 * Frames the invisible problem: most teams burn days on API onboarding
 * before writing the first useful integration. Names the product as
 * the answer: SUM's MCP server gives Claude Code the full API context,
 * so "ask in plain language → get production-ready code" replaces
 * "read docs → guess → debug".
 *
 * Cyan accent — MCP / developer experience belongs on brand-primary.
 */

import Image from 'next/image';
import React from 'react';
import { Locale } from '../../../../i18n-config';
import { useTranslation } from '@/i18n/client';
import FeatureHero from '../_shared/sections/FeatureHero';

type Props = { lang: Locale };

const MCP_CLAUDE_IMAGE = 'https://cdn.1cdn.app/application/UMD/26033023472260_claude_sum.png';

const McpClaudeHero = ({ lang }: Props) => {
  const { t } = useTranslation(lang);

  const trust = t('McpClaude/Hero/trust', { returnObjects: true }) as string[] | string;

  const visual = (
    <Image
      src={MCP_CLAUDE_IMAGE}
      alt={t('McpClaude/BeforeAfter/image_alt')}
      width={840}
      height={840}
      sizes="(min-width: 1024px) 320px, 70vw"
      className="mx-auto h-auto w-3/4 max-w-[320px] rounded-3xl lg:w-full"
      priority
    />
  );

  return (
    <FeatureHero
      accent="#22D3EE"
      ariaLabel={t('McpClaude/Hero/aria_label')}
      copy={{
        kicker: t('McpClaude/Hero/badge'),
        headlineA: t('McpClaude/Hero/title1'),
        headlineB: t('McpClaude/Hero/title2'),
        subhead: t('McpClaude/Hero/subtitle'),
        ctaPrimary: {
          label: t('McpClaude/Hero/cta_primary'),
          href: `/${lang}/contact-us`,
        },
        ctaSecondary: {
          label: t('McpClaude/Hero/cta_secondary'),
          href: '#how-it-works',
        },
        trust: Array.isArray(trust) ? trust : undefined,
      }}
      visual={visual}
      visualVisibleOnMobile
    />
  );
};

export default McpClaudeHero;
